import React, { useCallback } from 'react';
import { Menu, Item, Separator, Submenu, animation } from 'react-contexify';
import { useRockFetchPost } from 'utils/RockFetch';
import { ProjectNodeProps, TreeNodeExpanded } from 'business/models/CustomModels';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import TreeNode from './TreeNode';
import WithContextMenu from './WithContextMenu';
import './Menu.scss';
import {
  CreateNewTreeNodeDTO,
  PFDocProteinFormulationDTO,
  PFDocSaveProteinFormulationDTO,
  PFStateDTO,
  TreeNodeDTO,
} from 'business/models/Models';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import useModel from 'business/models/useModel';
import ProteinFormulationService from 'business/services/ProteinFormulationService';
import ScreenService from 'business/services/ScreenService';
import { DraggableContainer } from 'components/DraggableContainer';
import { usePasteNodesHandler } from 'components/explorer/hooks';
import ETreeActionTypes from 'enums/ETreeActionTypes';
import { copyPasteValidation } from 'components/explorer/utils';
import useRMLite from 'CustomHooks/useRMLite';

export enum ECreateNodeType {
  None = 'None',
  ProjectFolder = 'ProjectFolder',
  Project = 'Project',
  SynchrotronTripv4Folder = 'SynchrotronTripv4Folder',
  SearchesFolder = 'SearchesFolder',
}
export default function ProjectNode(props: ProjectNodeProps) {
  const node = props.data;
  const { onRefreshTreeNode, treeControlState, externalDragHandling } = props;

  const ContextifiedTreeNode = WithContextMenu(TreeNode);
  const [rockFetchPost] = useRockFetchPost();
  const pasteNodesHandler = usePasteNodesHandler();
  const [saveProteinFormulation] = useModel(
    ProteinFormulationService,
    'saveProteinFormulation',
  );
  const rmLite = useRMLite();

  async function createScreenExperiment() {
    const body = {
      parentTreeNodeId: node.id,
    };
    const newExp = await rockFetchPost('/api/experiment/createscreenexperiment', body);
    if (newExp === null) {
      return null;
    }
    await props.newNode(newExp, node.children, node.id, node);

    window.open(`/exp/${newExp.extra.id}/info`, `/exp/${newExp.extra.id}`); // to open new page
    return newExp;
  }
  async function createNewScreen() {
    const screenService = new ScreenService();
    const treeNodeDTO: TreeNodeDTO = await screenService.createScreen(node.id);
    if (treeNodeDTO === null) {
      return null;
    }
    await props.newNode(treeNodeDTO, node.children, node.id, node);
    window.open(`/screen/${treeNodeDTO.extra.id}`, `/screen/${treeNodeDTO.extra.id}`);
    return treeNodeDTO;
  }

  async function onAddItem({ event }, type) {
    event.stopPropagation();

    props.onContextMenuClick(event, node, type);
    switch (type) {
      case ETreeNodeTypes.Experiment:
        await createScreenExperiment();
        break;
      case ETreeNodeTypes.Protein:
        createNewProtein();
        break;
      case ETreeNodeTypes.Screen:
        createNewScreen();
        break;
    }
  }
  function loadImageIcon(fileName, type = 'png') {
    const image = require(`components/main/images/${fileName}.${type}`);
    return (
      <img
        id="project-node-icon-image"
        style={{ marginRight: 15 }}
        alt="Not Found"
        src={image}
        width="25"
        height="25"
      />
    );
  }

  const NodeMenu = useCallback(() => {
    const isProjectsFolderNode =
      props.newFolderType == ETreeNodeTypes.ProjectsFolder ||
      props.newFolderType == ETreeNodeTypes.Project;
    return (
      <Menu
        id={`project-node-menu${node.id}`}
        animation={animation.zoom}
        className="projectNodeMenu"
      >
        <Submenu
          label={
            <div
              id={`project-node-submenu-label${node.id}`}
              style={{ display: 'flex', alignItems: 'center', padding: 5 }}
            >
              {loadImageIcon('New')}Add Items
            </div>
          }
        >
          <Item
            className="newExpCommand"
            onClick={(e) => onAddItem(e, ETreeNodeTypes.Experiment)}
            disabled={
              node.accessRightName !== ETreeNodeAccessRightNames.Modify ||
              isProjectsFolderNode
            }
          >
            <div id={`project-node-experiment-menu-icon${node.id}`} className="menuItem">
              {loadImageIcon('ExperimentMenuIcon')}New Experiment
            </div>
          </Item>
          <Separator />
          <Item
            onClick={(e) => onAddItem(e, ETreeNodeTypes.Screen)}
            disabled={node.accessRightName !== ETreeNodeAccessRightNames.Modify}
          >
            <div id={`project-node-screen-zoomed-icon${node.id}`} className="menuItem">
              {loadImageIcon('Screen-Zoomed-Icon', 'svg')}New Screens
            </div>
          </Item>
          <Separator />
          <Item
            onClick={(e) => onAddItem(e, ETreeNodeTypes.Protein)}
            disabled={
              node.accessRightName !== ETreeNodeAccessRightNames.Modify ||
              isProjectsFolderNode
            }
          >
            <div id={`project-node-protein-icon${node.id}`} className="menuItem">
              {loadImageIcon('Protein')}New Protein
            </div>
          </Item>
          <Separator />
          <Item>
            <div id={`project-node-folder-icon${node.id}`} className="menuItem">
              {loadImageIcon('Folder')}New Folder
            </div>
          </Item>
          <Separator />
          {node.type === ETreeNodeTypes.Screens ||
          node.type === ETreeNodeTypes.ScreensFolder ? null : (
            <Item
              disabled={
                node.type === ETreeNodeTypes.Project ||
                node.type === ETreeNodeTypes.ProjectFolder
              }
            >
              <div
                id={`project-node-project-context-icon${node.id}`}
                className="menuItem"
              >
                {loadImageIcon('Project_Context')}New Project
              </div>
            </Item>
          )}
        </Submenu>
        <Separator />
        <Item className="pasteCommand" onClick={(e) => props.onRefreshTreeNode(node)}>
          <div id={`project-node-refresh-icon${node.id}`} className="menuItem">
            {loadImageIcon('Refresh')}Refresh
          </div>
        </Item>
        <Separator />
        <Item
          className="pasteCommand"
          disabled={node.accessRightName !== ETreeNodeAccessRightNames.Modify || true}
        >
          <div id={`project-node-cut-icon${node.id}`} className="menuItem">
            {loadImageIcon('Cut')}Cut
          </div>
        </Item>
        <Item className="copyCommand" disabled={true}>
          <div id={`project-node-copy-icon${node.id}`} className="menuItem">
            {loadImageIcon('Copy')}Copy
          </div>
        </Item>
        <Item
          className="pasteCommand"
          disabled={node.accessRightName !== ETreeNodeAccessRightNames.Modify || true}
        >
          <div id={`project-node-paste-icon${node.id}`} className="menuItem">
            {loadImageIcon('Paste')}Paste
          </div>
        </Item>
        <Item
          className="renameCommand"
          disabled={node.accessRightName !== ETreeNodeAccessRightNames.Modify || true}
        >
          <div id={`project-node-rename-icon${node.id}`} className="menuItem">
            {loadImageIcon('Rename')}Rename
          </div>
        </Item>
        <Separator />
        <Item
          className="barcodeCommand"
          disabled={node.accessRightName !== ETreeNodeAccessRightNames.NodeVisible}
        >
          <div id={`project-node-barcode-icon${node.id}`} className="menuItem">
            {loadImageIcon('Barcode')}Print Barcode
          </div>
        </Item>
        <Item
          className="analyzeCommand"
          disabled={
            node.accessRightName !== ETreeNodeAccessRightNames.NodeVisible || true
          }
        >
          <div id={`project-node-analyze-icon${node.id}`} className="menuItem">
            {loadImageIcon('Analyze')}Analyze
          </div>
        </Item>
        <Item className="displayStorageCommand" disabled={true}>
          <div id={`project-node-display-and-usage-icon${node.id}`} className="menuItem">
            {loadImageIcon('DisplayAndUsage')}Display Usage Storage
          </div>
        </Item>
        <Item
          className="hitsCommand"
          disabled={
            !(node.accessRightName === ETreeNodeAccessRightNames.NodeVisible) || true
          }
        >
          <div id={`project-node-hits-report-icon${node.id}`} className="menuItem">
            {loadImageIcon('HitsReport')}Hits Report
          </div>
        </Item>
        <Separator />
        <Item
          className="removeCommand"
          disabled={node.accessRightName !== ETreeNodeAccessRightNames.Modify || true}
        >
          <div id={`project-node-remove-icon${node.id}`} className="menuItem">
            {loadImageIcon('Remove')}Remove
          </div>
        </Item>
      </Menu>
    );
  }, [node]);

  const allProps = {
    ...props,
    menu: NodeMenu,
  };

  async function createNewProtein() {
    const saveData: PFDocSaveProteinFormulationDTO = new PFDocSaveProteinFormulationDTO();
    saveData.proteinFormulation = new PFDocProteinFormulationDTO();
    saveData.proteinFormulation.pfName = '';
    saveData.proteinFormulation.datePrepared = new Date();
    saveData.proteinFormulation.pfState = PFStateDTO.New;
    saveData.deletedPFIngs = [];
    saveData.deletedStIngs = [];
    saveData.parentNodeId = node.id;

    const res: PFDocProteinFormulationDTO = await saveProteinFormulation(saveData);
    if (res === null) {
      return null;
    }
    const newNode: CreateNewTreeNodeDTO = new CreateNewTreeNodeDTO();
    newNode.id = res.treeNodeId;
    newNode.name = res.pfName;
    newNode.parentId = node.id;
    newNode.type = ETreeNodeTypes.Protein;

    await props.newNode(newNode, node.children, node.id, node);
    window.open(`/protein/${res.treeNodeId}`, `/protein/${res.treeNodeId}`);
  }

  const postRefreshMessage = (sourceNode) => () => {
    onRefreshTreeNode(
      { id: sourceNode.parentId },
      treeControlState.selectedExplorerNode.id === sourceNode.parentId,
    );
    onRefreshTreeNode(node, treeControlState.selectedExplorerNode.id === node.id);
  };

  const handleDrop = (nodes: TreeNodeExpanded[], operationType: ETreeActionTypes) => {
    if (nodes) {
      pasteNodesHandler(operationType, nodes, node, postRefreshMessage(nodes[0]), false);
    }
  };

  const validateDropFile = (
    draggedNodes: TreeNodeExpanded[],
    currentNode: TreeNodeExpanded,
  ) => {
    const isQuickAccess = currentNode.extra?.quickAccessMeta?.originalId > 0;
    if (isQuickAccess) {
      return false;
    }
    return copyPasteValidation(draggedNodes, currentNode).unableToPasteNodes.length <= 0;
  };

  return (
    <>
      {props.isShowContext ? (
        <ContextifiedTreeNode key={node.id} {...allProps} />
      ) : rmLite ? (
        <TreeNode {...allProps} />
      ) : (
        <DraggableContainer
          id="project-node-draggablecontainer"
          node={node}
          nodeName={node.name}
          handleDrop={handleDrop}
          selectedFiles={[node]}
          validateDrop={validateDropFile}
          externalDragHandling={externalDragHandling}
        >
          <TreeNode {...allProps} />
        </DraggableContainer>
      )}
    </>
  );
}
